import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Plus, Frame } from "lucide-react";
import { API, fmtPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import FramedPreview from "@/components/FramedPreview";
import { Reveal, HeroLines, Eyebrow, EASE } from "@/components/Reveal";
import { toast } from "sonner";

const CHAPTERS = [
  { n: "I", title: "The Land in Watercolor", text: "Every painting begins as ground pigment carried by water across 140 lb Arches watercolor paper — violet ridges, still water, soft valley skies. The land paints itself; I only hold the brush." },
  { n: "II", title: "Tactile Memory", text: "Painted in the studio from plein-air sketches and field photographs, each work carries the day it was made: valley fog, coastal wind off the Pacific, the last band of amber dusk." },
  { n: "III", title: "Archival Living", text: "Affordable original art for everyone — each 3 × 5 inch card suited for framing, made to be lived with for generations, and priced to be lived with now." },
];

export default function Home() {
  const [cards, setCards] = useState([]);
  const [preview, setPreview] = useState(null);
  const [journal, setJournal] = useState({ name: "", email: "" });
  const { add } = useCart();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);

  useEffect(() => {
    API.get("/notecards").then((r) => setCards(r.data)).catch(() => {});
  }, []);

  const joinJournal = async (e) => {
    e.preventDefault();
    try {
      await API.post("/contact", { ...journal, topic: "journal", message: "Please add me to the Studio Journal list." });
      toast.success("Welcome to the Studio Journal", { description: "Dispatches from the Studio arrive a few times a year, never more." });
      setJournal({ name: "", email: "" });
    } catch {
      toast.error("Could not sign you up just now.");
    }
  };

  return (
    <div data-testid="home-page">
      <Seo description="Original landscape watercolor notecards — each hand-painted and one of one — from the Willamette Valley studio of Richard Kuensting." />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[92vh] overflow-hidden bg-night flex flex-col" style={{ minHeight: "max(620px, 92vh)" }} data-testid="hero-section">
        <motion.div style={{ y: imgY }} className="absolute inset-0">
          <motion.img
            src="/images/hero-indigo.jpg"
            alt="Indigo Swell — original watercolor seascape by Richard Kuensting"
            className="w-full h-full object-cover"
            initial={{ scale: 1.12, opacity: 0.6 }}
            animate={{ scale: 1.02, opacity: 1 }}
            transition={{ duration: 2.2, ease: EASE }}
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-night/85 via-night/30 to-night/25" />
        <div className="absolute bottom-0 left-0 right-0 h-[70%] bg-gradient-to-t from-night/70 via-night/25 to-transparent" />
        <motion.div style={{ y: textY }} className="relative z-10 flex-1 w-full max-w-7xl mx-auto px-5 sm:px-10 flex flex-col justify-end pt-28 sm:pt-32 pb-16 sm:pb-24">
          <motion.div
            initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 1.1, ease: EASE }}
            className="self-center text-center mb-10 sm:mb-14"
            data-testid="hero-banner"
          >
            <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.45em] text-linen [text-shadow:0_1px_14px_rgba(24,23,21,0.75)]">Studio of</p>
            <p className="font-serif italic text-2xl sm:text-4xl text-linen mt-2 [text-shadow:0_2px_20px_rgba(24,23,21,0.75)]">Richard Kuensting</p>
            <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.45em] text-linen mt-2 [text-shadow:0_1px_14px_rgba(24,23,21,0.75)]">Willamette Valley, Oregon</p>
          </motion.div>
          <HeroLines
            lines={["Land and water,", "rendered", "in pigment."]}
            className="font-serif font-light text-linen text-5xl sm:text-7xl lg:text-8xl leading-[1.02] tracking-tight [text-shadow:0_2px_30px_rgba(24,23,21,0.6)]"
            lineClassName=""
          />
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8, ease: EASE }}
            className="mt-6 max-w-xl text-linen/90 text-base sm:text-lg leading-relaxed [text-shadow:0_1px_18px_rgba(24,23,21,0.6)]"
          >
            Atmospheric landscape watercolors — each one an individually hand-painted notecard, one of one, from the easel to your wall.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8, ease: EASE }}
            className="mt-8 flex flex-wrap gap-4"
          >
            <Link to="/portfolio" data-testid="hero-explore-cta"
              className="inline-flex items-center gap-2 bg-linen text-ink rounded-full px-7 py-3.5 text-xs font-mono uppercase tracking-[0.2em] hover:bg-terracotta hover:text-linen transition-colors duration-300">
              Explore the Collection <ArrowRight size={14} />
            </Link>
            <Link to="/notecards" data-testid="hero-notecards-cta"
              className="inline-flex items-center gap-2 border border-linen/40 text-linen rounded-full px-7 py-3.5 text-xs font-mono uppercase tracking-[0.2em] hover:bg-linen hover:text-ink transition-colors duration-300">
              Browse Notecards
            </Link>
          </motion.div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
          className="absolute bottom-6 right-6 sm:right-10 text-linen/60"
        >
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}>
            <ArrowDown size={18} />
          </motion.div>
        </motion.div>
      </section>

      <Marquee />

      {/* MANIFESTO CHAPTERS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-10 py-24 sm:py-32" data-testid="manifesto-section">
        <Reveal>
          <Eyebrow>A Studio Manifesto</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-5xl text-ink mt-4 max-w-2xl leading-tight text-balance">
            Three convictions, carried in every wash
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-10 md:gap-8 mt-14 sm:mt-20">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.12}>
              <div className="border-t border-ink/15 pt-6">
                <span className="font-serif italic text-5xl text-terracotta/80">{c.n}.</span>
                <h3 className="font-serif text-2xl text-ink mt-4">{c.title}</h3>
                <p className="text-sm sm:text-base text-stone2 leading-relaxed mt-3">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* IMAGINE THE POSSIBILITIES — framed card inspiration */}
      <section className="bg-paper border-y border-ink/10" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-10 py-24 sm:py-32">
          <Reveal>
            <div className="grid lg:grid-cols-12 gap-10 items-center" data-testid="framing-inspiration">
              <div className="lg:col-span-4">
                <Eyebrow>Imagine the Possibilities</Eyebrow>
                <h2 className="font-serif text-3xl sm:text-5xl text-ink mt-4 leading-tight">Small artwork, big impact</h2>
                <p className="text-sm sm:text-base text-stone2 leading-relaxed mt-6 max-w-sm">
                  Any card can be framed to match your tastes and decor — a quiet wall, a bookshelf, a desk corner. A generous mat and a simple frame give a small original real presence.
                </p>
              </div>
              <div className="lg:col-span-8">
                <div>
                  <img src="/images/framed-closeup.jpg" alt="A hand-painted card from the studio, framed with a generous mat and rustic wood frame" loading="lazy"
                    data-testid="framed-example-closeup" className="w-full aspect-[4/3] object-cover border border-ink/10 deckle" />
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mt-3">A card from the table, framed</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* NOTE CARDS PREVIEW */}
      <section className="max-w-7xl mx-auto px-5 sm:px-10 py-24 sm:py-32" data-testid="notecards-preview">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>The Card Table</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-5xl text-ink mt-4 leading-tight">Paintings you can post</h2>
            <p className="text-stone2 mt-4 max-w-xl text-sm sm:text-base leading-relaxed">
              Every card is an original watercolor, individually hand painted on 140 lb Arches paper — no prints, no reproductions. When a card finds a home, it&rsquo;s gone.
            </p>
          </div>
          <Link to="/notecards" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-walnut hover:text-terracotta transition-colors">
            Browse all cards <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8 mt-14">
          {cards.filter((c) => c.status === "available").slice(-4).map((card, i) => (
            <Reveal key={card.id} delay={i * 0.08}>
              <div className="group">
                <div className="relative overflow-hidden bg-[#E8E0D0] border border-ink/10 deckle aspect-[4/5]">
                  <button type="button" onClick={() => setPreview(card)} data-testid={`home-card-preview-${card.id}`}
                    aria-label={`See ${card.title} framed`} className="block w-full h-full cursor-pointer">
                    <img src={card.image} alt={`${card.title} — original hand-painted watercolor notecard`} loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                  </button>
                  {card.status === "available" && (
                    <button
                      data-testid={`notecard-quick-add-${card.id}`}
                      onClick={() => add(card)}
                      aria-label={`Add ${card.title} to bag`}
                      className="absolute bottom-3 right-3 z-10 w-10 h-10 rounded-full bg-ink text-linen flex items-center justify-center opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-all duration-300 hover:bg-terracotta"
                    >
                      <Plus size={16} />
                    </button>
                  )}
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sage mt-4">
                  {card.status === "sold" ? "Sold — in a private collection" : card.status === "soon" ? "In the studio — soon" : "Original · one of one"}
                </p>
                <h3 className="font-serif text-lg sm:text-xl text-ink mt-1 leading-snug">{card.title}</h3>
                <p className="font-mono text-xs text-stone2 mt-1">{card.status === "sold" ? "—" : fmtPrice(card.price_cents)}</p>
                <button type="button" onClick={() => setPreview(card)} data-testid={`home-see-framed-${card.id}`}
                  className="mt-1.5 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.15em] text-stone2 hover:text-terracotta transition-colors">
                  <Frame size={12} /> See it framed
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FRAMED TEASER — removed per Richard: no framed gallery link, inspiration only */}

      {/* ARTIST INTRO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-10 py-24 sm:py-32" data-testid="artist-intro">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <img src="/images/richard-portrait.jpg" alt="Richard Kuensting — portrait"
                loading="lazy" className="w-full aspect-[4/5] object-cover border border-ink/10 deckle" />
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone2 mt-3">
                Fig. 01 — Richard Kuensting
              </p>
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <Eyebrow>The Artist</Eyebrow>
              <h2 className="font-serif text-3xl sm:text-5xl text-ink mt-4 leading-tight">
                &ldquo;I paint to capture windy days, foggy mornings and peaceful afternoons.&rdquo;
              </h2>
              <p className="text-stone2 mt-6 leading-relaxed text-sm sm:text-base max-w-xl">
                Richard has been making art since childhood — painting, sculpting and drawing. From his studio in Oregon&rsquo;s Willamette Valley, surrounded by fields and hills, he paints the remembered feelings of places he has visited. Every original, study, and notecard begins here, at this bench.
              </p>
              <Link to="/about" data-testid="artist-intro-cta"
                className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] border border-ink/20 rounded-full px-7 py-3.5 hover:bg-ink hover:text-linen transition-colors duration-300">
                Meet Richard <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* JOURNAL SIGNUP */}
      <section className="bg-cream border-t border-ink/10" data-testid="journal-section">
        <div className="max-w-3xl mx-auto px-5 sm:px-10 py-20 sm:py-28 text-center">
          <Reveal>
            <Eyebrow className="text-terracotta">The Studio Journal</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl text-ink mt-4 leading-tight">Dispatches from the Studio, a few times a year</h2>
            <p className="text-stone2 mt-4 text-sm sm:text-base">Notes from the studio, field studies, and travel journals — seasonal ramblings from a visual perspective, plus first word of new card releases and changes to the table.</p>
            <form onSubmit={joinJournal} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
              <input data-testid="journal-name-input" required placeholder="Name" value={journal.name}
                onChange={(e) => setJournal({ ...journal, name: e.target.value })}
                className="flex-1 bg-paper border border-ink/15 rounded-full px-5 py-3 text-sm focus:outline-none focus:border-terracotta/60" />
              <input data-testid="journal-email-input" required type="email" placeholder="Email" value={journal.email}
                onChange={(e) => setJournal({ ...journal, email: e.target.value })}
                className="flex-1 bg-paper border border-ink/15 rounded-full px-5 py-3 text-sm focus:outline-none focus:border-terracotta/60" />
              <button data-testid="journal-submit-btn" type="submit"
                className="bg-ink text-linen rounded-full px-7 py-3 text-xs font-mono uppercase tracking-[0.2em] hover:bg-terracotta transition-colors duration-300">
                Join
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      <FramedPreview card={preview} open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }} />
    </div>
  );
}
